import React from 'react';
import styled from 'styled-components';

// Error boundary especifica pro PracasMapa. React Error Boundaries
// ainda precisam ser class component em React 17 (sem hook equivalente).
//
// Caso o componente do mapa crashe (Google Maps API timeout, bug
// inesperado em algum handler, etc), em vez de tela branca + log
// no console, mostramos um fallback amigavel com botao 'Recarregar'.
// Crucial em CCO 24/7 — quando o operador chega no posto, o ambiente
// nao pode estar quebrado sem feedback.
class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log estruturado pra debug. Nao escala pra Sentry/etc por enquanto,
    // mas deixa rastro no console pra investigar.
    // eslint-disable-next-line no-console
    console.error('[PracasMapa] crashou:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Fallback>
          <FallbackTitle>O mapa encontrou um problema</FallbackTitle>
          <FallbackText>
            Algo inesperado aconteceu ao renderizar o mapa. Os dados
            continuam sendo coletados normalmente — recarregue para
            tentar novamente.
          </FallbackText>
          {this.state.error?.message && (
            <FallbackError>
              <strong>Detalhe técnico:</strong>{' '}
              {String(this.state.error.message).slice(0, 240)}
            </FallbackError>
          )}
          <FallbackActions>
            <FallbackButton type="button" onClick={this.handleRetry}>
              Tentar novamente
            </FallbackButton>
            <FallbackButtonSecondary
              type="button"
              onClick={() => window.location.reload()}
            >
              Recarregar página
            </FallbackButtonSecondary>
          </FallbackActions>
        </Fallback>
      );
    }
    return this.props.children;
  }
}

const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  width: 100%;
  padding: 32px;
  text-align: center;
  background: ${(props) => props.theme.colors.card};
  color: ${(props) => props.theme.colors.darkerblue};
`;

const FallbackTitle = styled.h2`
  font-size: 18px;
  font-weight: 800;
  margin: 0;
`;

const FallbackText = styled.p`
  font-size: 13px;
  color: ${(props) => props.theme.colors.label};
  max-width: 480px;
  margin: 0;
  line-height: 1.5;
`;

const FallbackError = styled.div`
  font-size: 11px;
  font-family: 'Courier New', monospace;
  color: ${(props) => props.theme.colors.red};
  background: ${(props) =>
    props.theme.title === 'light'
      ? 'rgba(0, 0, 0, 0.04)'
      : 'rgba(255, 255, 255, 0.04)'};
  padding: 8px 12px;
  border-radius: 6px;
  max-width: 520px;
  word-break: break-word;
`;

const FallbackActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const FallbackButton = styled.button`
  padding: 8px 16px;
  background: ${(props) => props.theme.colors.lightblue};
  border: 1px solid ${(props) => props.theme.colors.lightblue};
  border-radius: 6px;
  color: ${(props) => props.theme.colors.white};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: filter 120ms;

  &:hover {
    filter: brightness(1.1);
  }
`;

const FallbackButtonSecondary = styled(FallbackButton)`
  background: transparent;
  color: ${(props) => props.theme.colors.darkerblue};
  border-color: ${(props) => props.theme.colors.disabled};
`;

export default MapErrorBoundary;
